<template>
  <div v-if="showFrontendShell" class="app-shell bg-white text-ink">
    <Navbar />
    <Sidenav />
    <main class="app-main">
      <router-view />
    </main>
    <Footer />
  </div>
  <div v-else-if="showUserShell" class="user-shell bg-white text-ink">
    <UserNavbar />
    <main class="user-main">
      <router-view />
    </main>
    <UserFooter />
  </div>
  <div v-else-if="showVendorShell" class="vendor-shell bg-[#F8F3ED] text-ink">
    <VendorNavbar v-if="showVendorNavbar" />
    <main class="vendor-main" :class="{ 'vendor-main--plain': !showVendorNavbar }">
      <router-view />
    </main>
  </div>
  <router-view v-else />

  <Transition name="pwa-install-toast">
    <div v-if="showInstallToast" class="pwa-install-toast" role="status" aria-live="polite">
      <div class="pwa-install-toast__content">
        <img class="pwa-install-toast__logo" src="/assets/logo-icon.png" alt="Invi8">
        <div class="pwa-install-toast__copy">
          <p>Invi8</p>
          <span>invi8.com</span>
        </div>
      </div>
      <button type="button" class="pwa-install-toast__action" @click="installPwa">
        Add to home screen
      </button>
      <button type="button" class="pwa-install-toast__close" aria-label="Close install prompt" @click="dismissInstallToast">
        <i class="ri-close-line"></i>
      </button>
    </div>
  </Transition>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import Swal from 'sweetalert2'
import Navbar from '@/components/frontend/Navbar.vue'
import Footer from '@/components/frontend/Footer.vue'
import Sidenav from '@/components/frontend/Sidenav.vue'
import UserNavbar from '@/components/user/AppNavbar.vue'
import UserFooter from '@/components/user/AppFooter.vue'
import VendorNavbar from '@/components/vendor/AppNavbar.vue'
import userApi from '@/services/user/api'
import vendorApi from '@/services/vendor/api'
import { getAuthRole, getAuthUserId, isAuthenticated, setAuthSession, setAuthUser } from '@/services/auth'

const route = useRoute()
const showFrontendShell = computed(() => route.meta.layout === 'frontend')
const showUserShell = computed(() => route.meta.layout === 'user')
const showVendorShell = computed(() => route.meta.layout === 'vendor')
const showVendorNavbar = computed(() => route.meta.layout === 'vendor' && route.meta.requiresAuth)
const showInstallToast = ref(false)
const deferredInstallPrompt = ref(null)
const INSTALL_TOAST_DISMISSED_KEY = 'invi8_pwa_install_toast_dismissed'

const isStandaloneApp = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true

const isMobileOrTablet = () => window.matchMedia?.('(max-width: 1024px)').matches ?? false

const canShowInstallToast = () => {
  if (typeof window === 'undefined') return false
  if (!isMobileOrTablet()) return false
  if (isStandaloneApp()) return false
  return window.localStorage.getItem(INSTALL_TOAST_DISMISSED_KEY) !== '1'
}

const openInstallToast = () => {
  showInstallToast.value = canShowInstallToast()
}

const dismissInstallToast = () => {
  window.localStorage.setItem(INSTALL_TOAST_DISMISSED_KEY, '1')
  showInstallToast.value = false
}

const installPwa = async () => {
  if (deferredInstallPrompt.value) {
    const promptEvent = deferredInstallPrompt.value
    deferredInstallPrompt.value = null
    promptEvent.prompt()
    const choice = await promptEvent.userChoice
    if (choice?.outcome === 'accepted') {
      dismissInstallToast()
    }
    return
  }

  await Swal.fire({
    icon: 'info',
    title: 'Add to home screen',
    text: 'Open your browser menu and choose Add to Home Screen to install Invi8.',
    confirmButtonColor: '#FF7A00'
  })
}

const handleBeforeInstallPrompt = (event) => {
  event.preventDefault()
  deferredInstallPrompt.value = event
  openInstallToast()
}

const handleAppInstalled = () => {
  dismissInstallToast()
}

const handleInstallToastResize = () => {
  showInstallToast.value = canShowInstallToast() && showInstallToast.value
}

const resolveProfilePayload = (role, accountId) => {
  const payload = new FormData()
  if (role === 'vendor') {
    payload.append('vendor_id', accountId)
  } else {
    payload.append('s_id', accountId)
    payload.append('user_id', accountId)
  }
  return payload
}

const normalizeProfileUser = (data) => data?.user || data?.data?.user || data?.data || data?.profile || null

const loadProfile = async () => {
  if (!isAuthenticated()) return
  const accountId = getAuthUserId()
  const role = getAuthRole() || 'user'
  if (!accountId) return
  try {
    const payload = resolveProfilePayload(role, accountId)
    const { data } =
      role === 'vendor'
        ? await vendorApi.getProfileVendor(payload)
        : await userApi.getProfileApi(payload)
    const user = normalizeProfileUser(data)
    if (user) {
      setAuthUser({
        ...user,
        s_id:
          user?.s_id ||
          user?.id ||
          user?.user_id ||
          user?.vendor_id ||
          user?.vendorId ||
          accountId,
        role,
        type: role
      })
      setAuthSession({
        loggedIn: true,
        role,
        type: role,
        s_id:
          user?.s_id ||
          user?.id ||
          user?.user_id ||
          user?.vendor_id ||
          user?.vendorId ||
          accountId
      })
    }
  } catch {
    // ignore profile load failures
  }
}

watch(() => route.fullPath, loadProfile, { immediate: true })

onMounted(() => {
  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  window.addEventListener('appinstalled', handleAppInstalled)
  window.addEventListener('resize', handleInstallToastResize)

  window.setTimeout(() => {
    if (!deferredInstallPrompt.value) openInstallToast()
  }, 1200)
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  window.removeEventListener('appinstalled', handleAppInstalled)
  window.removeEventListener('resize', handleInstallToastResize)
})
</script>

<style>
:global(html, body, #app) {
  height: 100%;
} 

.user-shell {
  min-height: 100%;
  display: flex;
  flex-direction: column;
}
 
.app-main {
  min-height: calc(100vh - 120px);
}

.user-main {
  flex: 1 0 auto;
  width: 100%;
  min-height: calc(100vh - 220px);
  padding-top: 96px;
}

.user-main > * {
  width: 100%;
}

/* Bootstrap-style compatibility layer for legacy user-panel screens. */
.user-shell .container,
.user-shell .container-fluid,
.user-shell .container-lg {
  width: 100%;
  margin-right: auto;
  margin-left: auto;
  padding-right: 16px;
  padding-left: 16px;
}

.user-shell .container,
.user-shell .container-lg {
  max-width: 1140px;
}

.user-shell .container-fluid {
  max-width: 1280px;
}

.user-shell .row {
  display: flex;
  flex-wrap: wrap;
  margin-right: -12px;
  margin-left: -12px;
}

.user-shell .row.no-gutters {
  margin-right: 0;
  margin-left: 0;
}

.user-shell .row.no-gutters > [class*='col-'],
.user-shell .row.no-gutters > [class*=' col-'] {
  padding-right: 0;
  padding-left: 0;
}

.user-shell .row > [class*='col-'],
.user-shell .row > [class*=' col-'] {
  width: 100%;
  max-width: 100%;
  padding-right: 12px;
  padding-left: 12px;
  box-sizing: border-box;
}

.user-shell .col-3 { flex: 0 0 25%; max-width: 25%; }
.user-shell .col-4 { flex: 0 0 33.333333%; max-width: 33.333333%; }
.user-shell .col-5 { flex: 0 0 41.666667%; max-width: 41.666667%; }
.user-shell .col-6 { flex: 0 0 50%; max-width: 50%; }
.user-shell .col-7 { flex: 0 0 58.333333%; max-width: 58.333333%; }
.user-shell .col-8 { flex: 0 0 66.666667%; max-width: 66.666667%; }
.user-shell .col-10 { flex: 0 0 83.333333%; max-width: 83.333333%; }
.user-shell .col-12 { flex: 0 0 100%; max-width: 100%; }

.user-shell .d-none { display: none !important; }
.user-shell .d-flex { display: flex !important; }
.user-shell .d-block { display: block !important; }
.user-shell .w-100 { width: 100% !important; }
.user-shell .flex-grow-1 { flex-grow: 1 !important; }
.user-shell .flex-shrink-0 { flex-shrink: 0 !important; }
.user-shell .overflow-hidden { overflow: hidden !important; }
.user-shell .justify-content-center { justify-content: center !important; }
.user-shell .justify-content-between { justify-content: space-between !important; }
.user-shell .align-items-center { align-items: center !important; }
.user-shell .align-items-start { align-items: flex-start !important; }
.user-shell .text-center { text-align: center !important; }
.user-shell .text-right { text-align: right !important; }
.user-shell .text-muted,
.user-shell small.text-muted { color: #6b7280 !important; }
.user-shell .font-weight-bold { font-weight: 700 !important; }
.user-shell .font-weight-semibold { font-weight: 600 !important; }
.user-shell .small,
.user-shell small { font-size: 0.875rem; }
.user-shell .flex-wrap { flex-wrap: wrap !important; }
.user-shell .flex-nowrap { flex-wrap: nowrap !important; }
.user-shell .overflow-auto { overflow: auto !important; }
.user-shell .position-relative { position: relative !important; }
.user-shell .position-absolute { position: absolute !important; }
.user-shell .img-fluid { max-width: 100%; height: auto; }
.user-shell .rounded { border-radius: 0.75rem !important; }
.user-shell .rounded-circle { border-radius: 9999px !important; }
.user-shell .shadow-sm { box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08) !important; }
.user-shell .text-truncate {
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

.user-shell .btn-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.user-shell .btn-group > .btn,
.user-shell .btn-group > a {
  flex: 1 1 0;
}

.user-shell .spinner-border {
  display: inline-block;
  width: 1.25rem;
  height: 1.25rem;
  vertical-align: text-bottom;
  border: 0.16em solid rgba(15, 23, 42, 0.18);
  border-right-color: transparent;
  border-radius: 50%;
  animation: user-shell-spinner 0.75s linear infinite;
}

.user-shell .spinner-border-sm {
  width: 0.9rem;
  height: 0.9rem;
  border-width: 0.14em;
}

@keyframes user-shell-spinner {
  to {
    transform: rotate(360deg);
  }
}

.user-shell .m-0 { margin: 0 !important; }
.user-shell .p-0 { padding: 0 !important; }
.user-shell .p-1 { padding: 0.25rem !important; }
.user-shell .p-2 { padding: 0.5rem !important; }
.user-shell .p-3 { padding: 1rem !important; }
.user-shell .p-4 { padding: 1.5rem !important; }
.user-shell .p-5 { padding: 3rem !important; }
.user-shell .px-0 { padding-left: 0 !important; padding-right: 0 !important; }
.user-shell .px-2 { padding-left: 0.5rem !important; padding-right: 0.5rem !important; }
.user-shell .px-3 { padding-left: 1rem !important; padding-right: 1rem !important; }
.user-shell .px-4 { padding-left: 1.5rem !important; padding-right: 1.5rem !important; }
.user-shell .py-1 { padding-top: 0.25rem !important; padding-bottom: 0.25rem !important; }
.user-shell .py-2 { padding-top: 0.5rem !important; padding-bottom: 0.5rem !important; }
.user-shell .py-3 { padding-top: 1rem !important; padding-bottom: 1rem !important; }
.user-shell .py-4 { padding-top: 1.5rem !important; padding-bottom: 1.5rem !important; }
.user-shell .py-5 { padding-top: 3rem !important; padding-bottom: 3rem !important; }
.user-shell .pt-1 { padding-top: 0.25rem !important; }
.user-shell .pt-3 { padding-top: 1rem !important; }
.user-shell .pb-3 { padding-bottom: 1rem !important; }
.user-shell .pl-2 { padding-left: 0.5rem !important; }
.user-shell .pl-3 { padding-left: 1rem !important; }
.user-shell .pr-2 { padding-right: 0.5rem !important; }

.user-shell .mb-0 { margin-bottom: 0 !important; }
.user-shell .mb-1 { margin-bottom: 0.25rem !important; }
.user-shell .mb-2 { margin-bottom: 0.5rem !important; }
.user-shell .mb-3 { margin-bottom: 1rem !important; }
.user-shell .mb-4 { margin-bottom: 1.5rem !important; }
.user-shell .mb-5 { margin-bottom: 3rem !important; }
.user-shell .mt-1 { margin-top: 0.25rem !important; }
.user-shell .mt-2 { margin-top: 0.5rem !important; }
.user-shell .mt-3 { margin-top: 1rem !important; }
.user-shell .mt-4 { margin-top: 1.5rem !important; }
.user-shell .mt-5 { margin-top: 3rem !important; }
.user-shell .mr-1 { margin-right: 0.25rem !important; }
.user-shell .mr-2 { margin-right: 0.5rem !important; }
.user-shell .mr-3 { margin-right: 1rem !important; }
.user-shell .ml-1 { margin-left: 0.25rem !important; }
.user-shell .ml-2 { margin-left: 0.5rem !important; }

@media (min-width: 768px) {
  .user-shell .container,
  .user-shell .container-lg {
    padding-right: 20px;
    padding-left: 20px;
  }

  .user-shell .col-md-3 { flex: 0 0 25%; max-width: 25%; }
  .user-shell .col-md-4 { flex: 0 0 33.333333%; max-width: 33.333333%; }
  .user-shell .col-md-5 { flex: 0 0 41.666667%; max-width: 41.666667%; }
  .user-shell .col-md-6 { flex: 0 0 50%; max-width: 50%; }
  .user-shell .col-md-7 { flex: 0 0 58.333333%; max-width: 58.333333%; }
  .user-shell .col-md-8 { flex: 0 0 66.666667%; max-width: 66.666667%; }
  .user-shell .col-md-10 { flex: 0 0 83.333333%; max-width: 83.333333%; }
}

@media (min-width: 992px) {
  .user-shell .container,
  .user-shell .container-lg {
    max-width: 1200px;
  }

  .user-shell .col-lg-4 { flex: 0 0 33.333333%; max-width: 33.333333%; }
  .user-shell .col-lg-5 { flex: 0 0 41.666667%; max-width: 41.666667%; }
  .user-shell .col-lg-7 { flex: 0 0 58.333333%; max-width: 58.333333%; }
  .user-shell .col-lg-8 { flex: 0 0 66.666667%; max-width: 66.666667%; }

  .user-shell .d-lg-flex { display: flex !important; }
  .user-shell .d-lg-none { display: none !important; }
}

@media (min-width: 1200px) {
  .user-shell .d-xl-inline { display: inline !important; }
}

@media (max-width: 767px) {
  .user-shell .col-3,
  .user-shell .col-4,
  .user-shell .col-5,
  .user-shell .col-6,
  .user-shell .col-7,
  .user-shell .col-8,
  .user-shell .col-10 {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

.vendor-main {
  min-height: 100vh;
  padding-top: 98px;
}

.vendor-main--plain {
  padding-top: 0;
}

@media (max-width: 991px) {
  .user-main {
    padding-top: 82px;
  }

  .vendor-main {
    padding-top: 116px;
  }
}

@media (max-width: 575px) {
  .vendor-main {
    padding-top: 108px;
  }
}

a:hover {
  text-decoration: none!important;
}

.pwa-install-toast {
  position: fixed;
  right: 12px;
  bottom: 12px;
  left: 12px;
  z-index: 9999;
  display: none;
  grid-template-columns: minmax(0, 1fr) auto 28px;
  align-items: center;
  gap: 10px;
  border: 1px solid #e6e0da;
  border-radius: 3px;
  background: #ffffff;
  padding: 9px 8px 9px 10px;
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.2);
}

.pwa-install-toast__content {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 9px;
}

.pwa-install-toast__logo {
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  border-radius: 9px;
  object-fit: contain;
}

.pwa-install-toast__copy {
  min-width: 0;
}

.pwa-install-toast__copy p,
.pwa-install-toast__copy span {
  display: block;
  overflow: hidden;
  margin: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pwa-install-toast__copy p {
  color: #1f2937;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
}

.pwa-install-toast__copy span {
  margin-top: 2px;
  color: #6b7280;
  font-size: 10px;
  line-height: 1.2;
}

.pwa-install-toast__action {
  min-width: 118px;
  border: 0;
  border-radius: 2px;
  background: #2878d8;
  color: #ffffff;
  padding: 8px 10px;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;
  transition: background-color 0.2s ease;
}

.pwa-install-toast__action:hover {
  background: #1767c5;
}

.pwa-install-toast__close {
  display: inline-flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: #6b7280;
  font-size: 20px;
  line-height: 1;
}

.pwa-install-toast-enter-active,
.pwa-install-toast-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.pwa-install-toast-enter-from,
.pwa-install-toast-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@media (max-width: 1024px) {
  .pwa-install-toast {
    display: grid;
  }
}

@media (max-width: 360px) {
  .pwa-install-toast {
    grid-template-columns: minmax(0, 1fr) 28px;
  }

  .pwa-install-toast__action {
    grid-column: 1 / -1;
    width: 100%;
  }
}
</style>
